"use client";

import { useEffect, useMemo, useState } from "react";
import { getCategoryIcon, getCategoryIconForCategory } from "@/lib/category-icons";
import type { LucideIcon } from "lucide-react";

export type MetricEntry = {
  field: string;
  header: string;
  short_name: string | null;
  emoji: string | null;
  icon_name: string | null;
  category: string;
  subcategory: string | null;
  description: string | null;
  formula: string | null;
  interpretation: string | null;
  direction: "higher_better" | "lower_better" | "context_dependent" | null;
  reference_url: string | null;
};

type Group = {
  category: string;
  blurb: string | null;
  items: MetricEntry[];
};

// Subcategory data lives in column_meta.subcategory (managed via SQL).
// Display order only is hardcoded — it's UI presentation, not data.
const SUBCATEGORY_ORDER: Record<string, string[]> = {
  Strategy: ["Composite", "Pure factors"],
  Growth: ["Revenue", "EPS"],
  Quality: ["Margins", "Returns on capital", "Other quality"],
  Valuation: [
    "Price multiples",
    "Growth-adjusted multiples",
    "EV multiples — LTM",
    "EV multiples — forward",
    "EV multiples — FY3",
    "Yields",
  ],
  Dividend: ["Growth", "Payout"],
  Return: ["Short-term", "Long-term", "52-week range"],
  Financials: [
    "Income statement",
    "Cash flow",
    "Balance sheet",
    "Per-share & price",
    "Capital return",
    "Margins & multiples",
  ],
  Forecast: [
    "Revenue",
    "EBITDA",
    "EBIT",
    "Net Income",
    "Cash flow",
    "Per-share",
    "Margins",
    "Multiples",
  ],
};

// ----- Component -------------------------------------------------------------

export default function MetricsClient({ groups }: { groups: Group[] }) {
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | "All">("All");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Open the metric pointed to by the URL hash on load (e.g. /metrics#metric-PE).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash;
    if (hash.startsWith("#metric-")) {
      const field = decodeURIComponent(hash.slice("#metric-".length));
      setExpanded((prev) => {
        const next = new Set(prev);
        next.add(field);
        return next;
      });
      // Defer scroll until after expansion has rendered.
      setTimeout(() => {
        const el = document.getElementById(`metric-${field}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
    }
  }, []);

  const q = query.trim().toLowerCase();

  const toggle = (field: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      return next;
    });
    if (typeof history !== "undefined") {
      const url = new URL(window.location.href);
      url.hash = expanded.has(field) ? "" : `metric-${field}`;
      history.replaceState(null, "", url.toString());
    }
  };

  const filteredGroups = useMemo(() => {
    return groups
      .filter((g) => activeCategory === "All" || g.category === activeCategory)
      .map((g) => ({
        ...g,
        items: g.items.filter((m) => {
          if (!q) return true;
          return (
            m.header.toLowerCase().includes(q) ||
            m.field.toLowerCase().includes(q) ||
            (m.short_name && m.short_name.toLowerCase().includes(q)) ||
            (m.description && m.description.toLowerCase().includes(q)) ||
            (m.formula && m.formula.toLowerCase().includes(q))
          );
        }),
      }))
      .filter((g) => g.items.length > 0);
  }, [groups, q, activeCategory]);

  const totalShown = filteredGroups.reduce((acc, g) => acc + g.items.length, 0);
  const searchActive = q.length > 0;

  return (
    <>
      {/* Sticky search + category chips */}
      <div className="sticky top-0 z-20 -mx-4 md:-mx-6 px-4 md:px-6 py-4 bg-[#0E0E0E]/95 backdrop-blur-xl border-b border-white/5 mb-10">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="relative flex-1 min-w-0">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" strokeLinecap="round" />
            </svg>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search metrics — e.g. P/E, ROE, dividend yield, momentum…"
              className="w-full bg-white/[0.04] border border-white/10 rounded-lg pl-10 pr-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/30 focus:border-white/20"
            />
          </div>
          <div className="text-xs text-white/40 whitespace-nowrap">
            {totalShown} metric{totalShown === 1 ? "" : "s"}
            {expanded.size > 0 && (
              <button
                type="button"
                onClick={() => setExpanded(new Set())}
                className="ml-3 text-white/40 hover:text-white/70 underline underline-offset-2"
              >
                Collapse all
              </button>
            )}
          </div>
        </div>

        {/* Category chips */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          <CategoryChip
            label="All"
            active={activeCategory === "All"}
            onClick={() => setActiveCategory("All")}
          />
          {groups.map((g) => {
            const catIcon = getCategoryIconForCategory(g.category, g.items[0]?.icon_name);
            return (
              <CategoryChip
                key={g.category}
                label={g.category}
                count={g.items.length}
                active={activeCategory === g.category}
                onClick={() => setActiveCategory(g.category)}
                Icon={catIcon}
              />
            );
          })}
        </div>
      </div>

      {/* Empty state */}
      {filteredGroups.length === 0 && (
        <div className="text-center py-16 text-white/50 text-sm">
          No metrics match your search.
        </div>
      )}

      {/* Sections */}
      <div className="space-y-14">
        {filteredGroups.map((group) => (
          <CategorySection
            key={group.category}
            group={group}
            expanded={expanded}
            searchActive={searchActive}
            onToggle={toggle}
          />
        ))}
      </div>
    </>
  );
}

// ----- Subcomponents ---------------------------------------------------------

function CategorySection({
  group,
  expanded,
  searchActive,
  onToggle,
}: {
  group: Group;
  expanded: Set<string>;
  searchActive: boolean;
  onToggle: (field: string) => void;
}) {
  // Group items by subcategory (from column_meta.subcategory).
  // Items without a subcategory render in an unlabelled block at the top.
  const bySub = new Map<string, MetricEntry[]>();
  for (const m of group.items) {
    const sub = m.subcategory ?? "";
    if (!bySub.has(sub)) bySub.set(sub, []);
    bySub.get(sub)!.push(m);
  }

  const orderedSubs: string[] = (() => {
    const known = SUBCATEGORY_ORDER[group.category] ?? [];
    const seen = new Set(bySub.keys());
    const ordered = known.filter((s) => seen.has(s));
    const extras = Array.from(seen)
      .filter((s) => !known.includes(s))
      .sort((a, b) => {
        if (a === "") return 1;
        if (b === "") return -1;
        return a.localeCompare(b);
      });
    return [...ordered, ...extras];
  })();

  const SectionIcon = getCategoryIconForCategory(group.category, group.items[0]?.icon_name);

  return (
    <section id={`cat-${group.category}`}>
      <header className="mb-5">
        <h2 className="text-2xl font-semibold text-white/95 flex items-center gap-2.5">
          {SectionIcon && (
            <SectionIcon size={22} strokeWidth={1.6} style={{ color: "rgba(255,255,255,0.45)" }} />
          )}
          {group.category}
          <span className="text-sm font-normal text-white/40">
            {group.items.length} metric{group.items.length === 1 ? "" : "s"}
          </span>
        </h2>
        {group.blurb && (
          <p className="text-sm text-white/55 mt-2 max-w-3xl leading-relaxed">{group.blurb}</p>
        )}
      </header>

      <div className="space-y-6">
        {orderedSubs.map((sub) => {
          const items = bySub.get(sub) ?? [];
          return (
            <div key={sub || "_none"}>
              {sub && (
                <h3 className="text-[11px] uppercase tracking-[0.15em] text-white/40 font-semibold mb-2.5 pb-1.5 border-b border-white/[0.06]">
                  {sub}
                </h3>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
                {items.map((m) => (
                  <MetricRow
                    key={m.field}
                    metric={m}
                    isOpen={expanded.has(m.field) || (searchActive && hasContent(m))}
                    onToggle={() => onToggle(m.field)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function hasContent(m: MetricEntry): boolean {
  // When searching, auto-open rows that have any text content to surface matches.
  return Boolean(m.description || m.formula || m.interpretation);
}

function MetricRow({
  metric,
  isOpen,
  onToggle,
}: {
  metric: MetricEntry;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div
      id={`metric-${metric.field}`}
      className={`border-b border-white/[0.05] ${isOpen ? "py-3" : ""}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 py-2 text-left text-sm group hover:text-white text-white/85"
      >
        {(() => {
          const Icon = getCategoryIcon(metric.icon_name);
          if (Icon) return <Icon size={14} strokeWidth={1.6} className="shrink-0 text-white/40 group-hover:text-white/60" />;
          if (metric.emoji) return <span className="text-base shrink-0" aria-hidden="true">{metric.emoji}</span>;
          return null;
        })()}
        <span className="flex-1 truncate group-hover:text-white">{metric.header}</span>
        <svg
          className={`w-3.5 h-3.5 shrink-0 text-white/30 group-hover:text-white/60 transition-transform ${
            isOpen ? "rotate-90" : ""
          }`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path d="m9 6 6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {isOpen && (
        <div className="pl-7 pr-1 pb-3 space-y-3 text-xs">
          <p className="text-[10px] uppercase tracking-wider text-white/35 font-mono">
            {metric.field}
          </p>

          {metric.description ? (
            <p className="text-white/75 leading-relaxed text-[13px]">{metric.description}</p>
          ) : (
            <p className="text-white/40 italic leading-relaxed">Description coming soon.</p>
          )}

          {metric.formula && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-white/35 mb-1">Formula</p>
              <code className="block text-white/80 font-mono bg-white/[0.03] border border-white/[0.06] rounded px-2.5 py-1.5 leading-relaxed text-[12px]">
                {metric.formula}
              </code>
            </div>
          )}

          {metric.interpretation && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-white/35 mb-1">
                How to read
              </p>
              <p className="text-white/60 leading-relaxed text-[12px]">{metric.interpretation}</p>
            </div>
          )}

          {metric.reference_url && (
            <a
              href={metric.reference_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block text-[11px] text-white/40 hover:text-white/80 underline underline-offset-2"
            >
              Further reading →
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function CategoryChip({
  label,
  count,
  active,
  onClick,
  Icon,
}: {
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
  Icon?: LucideIcon | null;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors whitespace-nowrap ${
        active
          ? "bg-white text-black border-white"
          : "bg-white/[0.04] text-white/70 border-white/10 hover:bg-white/[0.08] hover:text-white"
      }`}
    >
      {Icon && (
        <Icon
          size={13}
          strokeWidth={1.8}
          style={{ color: active ? "inherit" : "rgba(255,255,255,0.45)" }}
        />
      )}
      {label}
      {count != null && (
        <span className={`ml-0.5 ${active ? "text-black/50" : "text-white/40"}`}>{count}</span>
      )}
    </button>
  );
}
